/* =====================================================================
   Koshnika — shared cart helper

   WHY THIS EXISTS
   Every shoppable surface (product carousel, Worn in Motion tiles, the
   lightbox, the floating video modal) carried its own copy of the same
   fetch('/cart/add.js') block, and every one of them finished with
   window.location.href = '/cart'.

   That redirect is the most expensive UX decision in the theme: a customer
   browsing a 72-saree catalogue is thrown out of the grid on every single
   add, so building a two or three piece order means navigating back each
   time. Most people simply don't bother.

   HOW IT TAKES OVER WITHOUT EDITING EVERY SECTION
   The delegated listener below runs on the CAPTURE phase at document level,
   so it fires before the per-section bubble-phase handlers and calls
   stopPropagation(). Those older handlers therefore never run, and no
   section markup had to change. If a section is later rewritten to call
   KoshCart.add() directly, this still behaves identically.

   Recognised buttons: [data-add], [data-vid], [data-variant].
   Anchors are ignored so "Sold out" links still navigate normally.

   EVENTS (added Sep 2026)
   Because this handler swallows the click, the per-section handlers it
   replaces never run — and neither did the events they used to fire.
   sections/koshnika-cart-progress.liquid and blocks/koshnika-floating-video
   both listen for 'cart:updated', so the discount progress bar silently
   stopped refreshing after an add from any shoppable surface. Both event
   names are now dispatched from paintCount(), so every listener in the
   theme is notified exactly once per add regardless of which surface the
   shopper used.
   ===================================================================== */

(function (w, d) {
  if (w.KoshCart) return;

  var TOAST_ID = 'kosh-toast';

  /* ---------- header cart count ---------- */

  function paintCount(count) {
    var nodes = d.querySelectorAll(
      '.cart-count-bubble span[aria-hidden], .cart-count, [data-cart-count], .header__cart-count'
    );
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = count;
    }

    var bubble = d.querySelector('.cart-count-bubble');
    if (bubble) bubble.hidden = count === 0;

    d.dispatchEvent(new CustomEvent('kosh:cart:updated', { detail: { count: count } }));

    /* Legacy name the rest of the theme already listens for. See EVENTS above. */
    d.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: count } }));
  }

  /* ---------- toast ---------- */

  function toast(msg, opts) {
    opts = opts || {};
    var el = d.getElementById(TOAST_ID);

    if (!el) {
      el = d.createElement('div');
      el.id = TOAST_ID;
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      d.body.appendChild(el);
    }

    el.className = 'kosh-toast' + (opts.error ? ' kosh-toast--error' : '');
    el.innerHTML = '';

    if (opts.image) {
      var img = d.createElement('img');
      img.src = opts.image;
      img.alt = '';
      img.width = 34;
      img.height = 43;
      el.appendChild(img);
    }

    var txt = d.createElement('span');
    txt.className = 'kosh-toast__tx';
    txt.textContent = msg;
    el.appendChild(txt);

    if (!opts.error) {
      var link = d.createElement('a');
      link.className = 'kosh-toast__go';
      link.href = '/cart';
      link.textContent = 'View cart';
      el.appendChild(link);
    }

    clearTimeout(el.__t);
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    el.__t = setTimeout(function () {
      el.classList.remove('is-in');
    }, opts.error ? 4200 : 3600);
  }

  /* ---------- cart drawer, if the theme has one ---------- */

  function openDrawer() {
    var drawer =
      d.querySelector('cart-drawer-component') ||
      d.querySelector('cart-drawer') ||
      d.querySelector('#CartDrawer');

    if (!drawer) return false;

    if (typeof drawer.open === 'function') { drawer.open(); return true; }
    if (typeof drawer.show === 'function') { drawer.show(); return true; }

    return false;
  }

  /* ---------- main ---------- */

  function add(variantId, btn, opts) {
    opts = opts || {};

    var span = btn ? btn.querySelector('span') : null;
    var label = opts.label ||
                (btn ? (btn.dataset.kLabel || (span ? span.textContent : btn.textContent)) : 'Add');

    if (btn && !btn.dataset.kLabel) btn.dataset.kLabel = label;

    function setText(t) {
      if (span) { span.textContent = t; }
      else if (btn) { btn.textContent = t; }
    }

    if (btn) {
      if (btn.disabled) return Promise.resolve();
      btn.disabled = true;
      btn.classList.add('is-busy');
      setText('Adding…');
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: opts.quantity || 1 }] })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        var item = (data.items && data.items[0]) || data;

        if (btn) {
          btn.classList.remove('is-busy');
          btn.classList.add('is-done');
          setText('Added');
          setTimeout(function () {
            btn.classList.remove('is-done');
            btn.disabled = false;
            setText(label);
          }, 2000);
        }

        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            paintCount(cart.item_count);

            if (!openDrawer()) {
              toast(
                (item.product_title || 'Added') + ' — added to cart',
                { image: item.image || opts.image }
              );
            }
            return cart;
          });
      })
      .catch(function (err) {
        if (btn) {
          btn.classList.remove('is-busy');
          btn.disabled = false;
          setText(label);
        }
        var msg = (err && (err.description || err.message)) ||
                  'Could not add that just now. Please try again.';
        toast(msg, { error: true });
      });
  }

  /* ---------- delegated capture-phase handler ---------- */

  d.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add], [data-vid], [data-variant]');
    if (!btn) return;

    /* Leave real links alone — "Sold out" points at the product page */
    if (btn.tagName === 'A') return;

    var id = btn.dataset.add || btn.dataset.vid || btn.dataset.variant;
    if (!id) return;

    /* Beat the per-section handlers, which listen on the bubble phase */
    e.preventDefault();
    e.stopPropagation();

    add(id, btn, { image: btn.dataset.kImage });
  }, true);

  w.KoshCart = {
    add: add,
    toast: toast,
    paintCount: paintCount
  };


(function(){
  var mq = window.matchMedia('(max-width:749px)');
  function collapse(){
    var cols = document.querySelectorAll('.kf-col');
    if (!cols.length) return false;
    if (mq.matches) {
      for (var i = 0; i < cols.length; i++) cols[i].open = false;
    }
    return true;
  }
  function watchForFooter(){
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function(){
      if (collapse()) obs.disconnect();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function(){ obs.disconnect(); }, 8000);
  }
  function boot(){
    if (!collapse()) watchForFooter();
    if (mq.addEventListener) {
      mq.addEventListener('change', function(e){ if (e.matches) collapse(); });
    } else if (mq.addListener) {
      mq.addListener(function(e){ if (e.matches) collapse(); });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

})(window, document);
